import React, { useState, useEffect } from 'react';

import PageLayout from 'layouts/PageLayout';
import Sidebar from 'scenes/Sidebar';
import CandidateProfile from 'scenes/CandidateProfile';
import InBounds from 'scenes/Sidebar/components/InBounds';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import UploadResumeForm from 'scenes/Form/components/UploadResumeForm';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import ProfileEditAvatarForm from 'scenes/Form/components/ProfileEditAvatarForm';
import ProfileItemReference from 'scenes/CandidateProfile/components/ProfileItemReference';
import ProfileItemRecommendation from 'scenes/CandidateProfile/components/ProfileItemRecommendation';
import PanelContainer from 'components/PanelContainer';
import BasicSection from 'components/BasicSection';
import BasicButton from 'components/BasicButton';
import Avatar from 'components/Avatar';
import LoadMask from 'components/LoadMask';

import { LinkBuilder } from 'helpers/link-builder';
import { DateHelpers } from 'helpers/date-helpers';
import { ENDPOINT } from 'constants/endpoints';
import { useApi } from 'hooks/useApi';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';

import './styles.css';

const ConfidentialInformation = props => {
  const {
    user,
    globalActions: { updateUserPicture, updateUserInboundsCount },
  } = props;

  const api = useApi();

  const [isFetching, setFetching] = useState(false);
  const [windowWidth, setWindowWidth] = useState(false);
  const [data, setData] = useState({
    PictureUrl: LinkBuilder.getCandidatePictureSizeLink(user.avatar, '160x160'),
  });

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    setFetching(true);
    api.get(
      ENDPOINT.FORM.GET.CONFIDENTIAL_INFORMATION,
      {},
      {
        handleSuccess: data => {
          setFetching(false);
          setData({ ...data });
        },
      }
    );
  }, []);

  const handleEditPhoto = () => {
    FormDialog({
      compact: true,
      modeless: false,
      popupTitleStyles: {
        padding: parseInt(windowWidth, 10) < 480 ? '0 20px' : '0 25px',
      },
      popupInnerStyles: {
        padding: parseInt(windowWidth, 10) < 480 ? '0' : '0 20px',
        marginTop: '45px',
      },
      popupStyles: {
        minHeight: '30vh',
        maxWidth: '452px',
      },
      popupContentStyles: {
        paddingTop: 0,
      },
      form: (actions, params) => (
        <ProfileEditAvatarForm actions={actions} params={params} />
      ),
    }).then(
      ({ PictureUrl }) => {
        if (PictureUrl) {
          updateUserPicture(PictureUrl);
          setData({
            ...data,
            PictureUrl: PictureUrl,
          });
        }
      },
      response => ResponseErrorDialog({ response })
    );
  };

  const handleDeletePhoto = () => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete your profile image?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.CANDIDATE_PROFILE_PICTURE_DELETE,
        {},
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            updateUserPicture(null);
            setData({
              ...data,
              PictureUrl: null,
            });
          },
        }
      );
    });
  };

  const handleUploadResume = () => {
    FormDialog({
      modeless: false,
      compact: false,
      popupStyles: {
        paddingBottom: '30px',
      },
      dismissWhenClose: true,
      form: (actions, params) => (
        <UploadResumeForm actions={actions} params={params} />
      ),
    }).then(
      () => {
        BasicDialog({
          bodyRender: (
            <p className="dialog__message">Resume was successfully uploaded.</p>
          ),
        });

        setData({
          ...data,
          ResumeUpdateData: DateHelpers.getCurrentDate(),
        });
      },
      response => ResponseErrorDialog({ response })
    );
  };

  const handleRecommendationCheckboxChange = (id, show) => {
    setData({
      ...data,
      Recommendations: data.Recommendations.map(recommendation =>
        recommendation.RecommendationID === id
          ? {
              ...recommendation,
              Active: show ? 1 : 0,
            }
          : recommendation
      ),
    });
  };

  const handleReferenceShowToFirms = (id, show) => {
    setData({
      ...data,
      References: data.References.map(reference =>
        reference.ReferenceID === id
          ? {
              ...reference,
              Active: show ? 1 : 0,
            }
          : reference
      ),
    });
  };

  const handleReferenceDelete = (id, remove) => {
    setData({
      ...data,
      References: data.References.map(reference =>
        reference.ReferenceID === id
          ? {
              ...reference,
              Deleted: remove ? 1 : 0,
            }
          : reference
      ),
    });
  };

  const handleSave = async () => {
    const params = await getParams();
    setFetching(true);
    api.post(ENDPOINT.FORM.SAVE.CONFIDENTIAL_INFORMATION, params, {
      handleError: response => ResponseErrorDialog({ response }),
      handleSuccess: () => {
        if (params.cbReferenceDelete) {
          setData({
            ...data,
            References: data.References.filter(
              reference => !reference.Deleted || Number(reference.Deleted) === 0
            ),
          });
        }
        BasicDialog({ bodyRender: <p>Your information has been updated</p> });
      },
      handleComplete: () => setFetching(false),
    });
  };

  const getParams = () => {
    const cbRecom = data.Recommendations.filter(
      recommendation => Number(recommendation.Active) === 1
    ).map(recommendation => recommendation.RecommendationID);

    const cbReference = data.References.filter(
      reference => Number(reference.Active) === 1
    ).map(reference => reference.ReferenceID);

    const cbReferenceDelete = data.References.filter(
      reference => Number(reference.Deleted) === 1
    ).map(reference => reference.ReferenceID);

    const hasDeleteAction = cbReferenceDelete.length > 0;
    const params = {};
    if (cbRecom.length > 0) {
      params.cbRecom = cbRecom;
    }

    if (cbReference.length > 0) {
      params.cbReference = cbReference;
    }

    return new Promise(resolve => {
      if (hasDeleteAction) {
        ConfirmDialog({
          bodyRender: (
            <p>Are you sure you want to delete selected references?</p>
          ),
          textSuccessBtn: 'Yes',
          textCancelBtn: 'No',
          dismissWhenClose: false,
        })
          .then(
            () => ({ ...params, cbReferenceDelete: cbReferenceDelete }),
            () => params
          )
          .then(params => resolve(params));
      } else {
        resolve(params);
      }
    });
  };

  const {
    References,
    Recommendations,
    FullName,
    PictureUrl,
    ResumeUpdateData,
  } = data;

  return (
    <PageLayout
      pageClass="confidential-information-page"
      pageTitle="Confidential Information"
      mainContent={
        <CandidateProfile>
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <PanelContainer
              styles={{ marginBottom: '10px', paddingTop: '45px' }}
            >
              <BasicSection title="Confidential Information">
                <div className="confidential-information-page__header">
                  <div className="confidential-information-page__avatar">
                    <div className="confidential-information-page__avatar--inner">
                      <Avatar
                        size={160}
                        name={FullName}
                        image={LinkBuilder.getCandidatePictureSizeLink(
                          PictureUrl,
                          '160x160'
                        )}
                      />
                      <div className="confidential-information-page__avatar-controls">
                        <label
                          className="confidential-information-page__avatar--upload"
                          onClick={handleEditPhoto}
                        />
                        {PictureUrl && (
                          <label
                            className="confidential-information-page__avatar--delete"
                            onClick={handleDeletePhoto}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="confidential-information-page__info">
                    <h4 className="confidential-information-page__subtitle">
                      Resume Last Uploaded
                    </h4>

                    <div className="confidential-information-page__text">
                      {ResumeUpdateData
                        ? `on ${ResumeUpdateData}`
                        : 'You have not yet uploaded a resume.'}
                    </div>

                    <div className="confidential-information-page__button">
                      <BasicButton
                        btnWidth={150}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnText="Update"
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={handleUploadResume}
                      />
                    </div>
                  </div>
                </div>
                {Recommendations && (
                  <div className="confidential-information-page__recommendations">
                    {isFetching && <LoadMask relative />}
                    <h3 className="confidential-information-page__subtitle">
                      Manage Recommendations
                    </h3>

                    <div className="confidential-information-page__list">
                      {Recommendations.map(item => (
                        <ProfileItemRecommendation
                          data={item}
                          editable
                          key={item.RecommendationID}
                          toggleFunc={handleRecommendationCheckboxChange}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {References && (
                  <div className="confidential-information-page__references">
                    {isFetching && <LoadMask relative />}
                    <h3 className="confidential-information-page__subtitle">
                      Manage References
                    </h3>
                    <div className="confidential-information-page__text">
                      Only 4 references will be shown on your profile page.
                    </div>

                    <div className="confidential-information-page__list confidential-information-page__list--row">
                      {References.map(item => (
                        <ProfileItemReference
                          data={item}
                          key={item.ReferenceID}
                          editable={true}
                          onShowToggle={handleReferenceShowToFirms}
                          onDelete={handleReferenceDelete}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="confidential-information-page__controls">
                  <div className="confidential-information-page__button">
                    <BasicButton
                      disabled={isFetching}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={600}
                      btnText="Save"
                      btnArrow={true}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleSave}
                    />
                  </div>
                </div>
              </BasicSection>
            </PanelContainer>
          </CSSTransition>
        </CandidateProfile>
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Sidebar>
            <InBounds
              inboundsCount={user.inbounds}
              onInboundsUpdate={updateUserInboundsCount}
            />
          </Sidebar>
        </CSSTransition>
      }
    />
  );
};

export default ConfidentialInformation;
